import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import useVacationLetterPDF from 'hooks/useVacationLetterPDF';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Pagination, Spinner, Table } from 'react-bootstrap';
import SoftBadge from '../../components/common/SoftBadge';

const parseSafeDate = (dateVal) => {
    if (!dateVal) return new Date();
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal === 'string') {
        const cleanStr = dateVal.split('T')[0]; // gets "YYYY-MM-DD"
        const parts = cleanStr.split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                return new Date(year, month - 1, day);
            }
        }
    }
    return new Date(dateVal);
};

const VacationRequestsTable = ({
    perfil,
    onVacationClick,
    refreshTrigger = 0,
}) => {
    const [loading, setLoading] = useState(false);
    const [vacacionesSolicitadas, setVacacionesSolicitadas] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    const { token } = useToken();
    const { generatePDF, isGenerating } = useVacationLetterPDF();
    const { showErrorToast, showSuccessToast } = useToast();

    const formatDate = (dateString) => {
        if (!dateString) return '-';

        try {
            let cleanDateString = dateString;
            if (typeof dateString === 'string' && dateString.includes('T')) {
                cleanDateString = dateString.split('T')[0];
            }
            const date = new Date(cleanDateString.replace(/-/g, '/'));
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                });
            }

            return dateString;
        } catch (error) {
            console.error("Error en formatDate:", error);
            return dateString || '-';
        }
    };

    /**
     * Carga vacaciones con paginación del lado del servidor
     */
    const loadVacaciones = async (pageNumber = 1, pageSize = 10) => {
        setLoading(true);

        try {
            const response = await fetch(
                `/api/vacaciones/ObtenerVacacionesSolicitadas?idUsuario=${perfil.idUsuario}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();
            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);

                const cabeceras = detalles.table2 || [];
                const paginationInfo = detalles.table3?.[0];
                const diasPlanos = detalles.table4 || [];

                const vacacionesPaginadas = cabeceras.map(vacacion => {
                    const fechasFiltradas = diasPlanos
                        .filter(d => (d.idVacacion || d.IdVacacion) === vacacion.idVacacion)
                        .map(d => d.dFechaVacaciones || d.DFechaVacaciones || d.dfechaVacaciones);

                    return {
                        ...vacacion,
                        dFechaVacaciones: fechasFiltradas
                    };
                });
                setVacacionesSolicitadas(vacacionesPaginadas);
                setTotalPages(paginationInfo.totalPages || 0);
                setTotalRecords(paginationInfo.totalRecords || 0);
                setCurrentPage(paginationInfo.currentPage || pageNumber);
            } else {
                setVacacionesSolicitadas([]);
                setTotalPages(0);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error al cargar vacaciones:', error);
            showErrorToast('Error de conexión al cargar las solicitudes de vacaciones');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Cargar datos iniciales
     */
    useEffect(() => {
        if (perfil && perfil.idUsuario) {
            setCurrentPage(1);
            loadVacaciones(1);
        }
    }, [perfil, refreshTrigger]);

    /**
     * Cambiar de página
     */
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
            loadVacaciones(pageNumber);
        }
    };

    const getStatusBadge = (estado) => {
        const estadoUpper = estado?.toUpperCase();
        const estados = {
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'APROBADA': { bg: 'success', text: 'Aprobada' },
            'RECHAZADA': { bg: 'danger', text: 'Rechazada' },
            'CANCELADA': { bg: 'secondary', text: 'Cancelada' }
        };

        const status = estados[estadoUpper] || { bg: 'secondary', text: estado };
        return <SoftBadge bg={status.bg}>{status.text}</SoftBadge>;
    };

    const handleRefresh = () => {
        loadVacaciones(currentPage);
    };

    const handleRowClick = (vacacion) => {
        const fotoUrl = perfil.vFoto ? perfil.vFoto : '/img/team/nofoto50.png';
        const diasDesglosados = vacacion.dFechaVacaciones || [];

        const fechaInicio = diasDesglosados.length > 0 ? diasDesglosados[0] : null;
        const fechaFin = diasDesglosados.length > 0 ? diasDesglosados[diasDesglosados.length - 1] : null;

        const vacationData = {
            idVacacion: vacacion.idSolicitudVacaciones || vacacion.idVacacion,
            idUsuario: perfil.idUsuario,
            name: `${perfil.vNombre || ''} ${perfil.vApellidos || ''}`.trim() || 'Usuario',
            position: perfil.vNombreTipoUsuario,
            photo: fotoUrl,
            iCategoria: perfil.iCategoria,
            daysRequested: vacacion.iDiasSolicitados || 0,
            estado: vacacion.vEstado,
            comentarios: vacacion.vComentarios || '',
            vMotivoSolicitud: vacacion.vMotivoSolicitud || '',
            vacationStart: fechaInicio,
            vacationEnd: fechaFin,
            dFechaVacaciones: JSON.stringify(diasDesglosados),
            dCreatedDate: vacacion.dCreatedDate,
            vUsuarioAprobador: vacacion.vUsuarioAprobador,
            //vPuestoUsuarioAprobador: vacacion.vPuestoUsuarioAprobador
        };

        if (onVacationClick) {
            onVacationClick(vacationData);
        }
    };

    const generateVacationLetterPDF = (vacacion) => {

        console.log("vacacion", vacacion)

        try {
            const diasDesglosados = vacacion.dFechaVacaciones || [];
            let fechasEspecificas = null;

            if (diasDesglosados.length > 0) {
                try {
                    fechasEspecificas = diasDesglosados.map(fechaStr => {
                        const dateObj = parseSafeDate(fechaStr);
                        return {
                            dateString: fechaStr,
                            formatted: dateObj.toLocaleDateString('es-ES', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            }),
                            isWeekend: [0, 6].includes(dateObj.getDay())
                        };
                    });
                } catch (e) {
                    console.warn('Error al parsear dFechaVacaciones en PDF:', e);
                }
            }
            const fixUTCString = (dateStr) => {
                if (typeof dateStr === 'string' && dateStr.includes('-') && !dateStr.includes('T')) {
                    return new Date(dateStr + 'T00:00:00');
                }
                return dateStr;
            };
            const inicioVacaciones = diasDesglosados.length > 0 ? diasDesglosados[0] : null;
            const finVacaciones = diasDesglosados.length > 0 ? diasDesglosados[diasDesglosados.length - 1] : null;

            const vacationData = {
                employeeName: `${perfil.vNombre || ''} ${perfil.vApellidos || ''}`.trim(),
                startDate: fixUTCString(inicioVacaciones),
                endDate: fixUTCString(finVacaciones),
                totalDays: vacacion.iDiasSolicitados || 1,
                specificDates: fechasEspecificas,
                requestDate: fixUTCString(vacacion.dCreatedDate),
                estado: vacacion.vEstado,
                vUsuarioAprobador: vacacion.vUsuarioAprobador,
                //vPuestoUsuarioAprobador: vacacion.vPuestoUsuarioAprobador

            };


            generatePDF(vacationData);
            showSuccessToast('Carta de solicitud de vacaciones generada exitosamente');
        } catch (error) {
            console.error('Error al generar el PDF:', error);
            alert('Error al generar el documento PDF. Por favor intente nuevamente.');
        }
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            items.push(<Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={loading} />);
        }

        items.push(<Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading} />);

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)} disabled={loading}>
                    {page}
                </Pagination.Item>
            );
        }

        items.push(<Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || loading} />);

        if (endPage < totalPages) {
            items.push(<Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={loading} />);
        }

        return items;
    };

    return (
        <div className="border rounded-3 p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                    <FontAwesomeIcon icon="calendar-check" className="me-2" />
                    Mis Solicitudes de Vacaciones
                    {totalRecords > 0 && (
                        <Badge bg="primary" pill className="ms-2">
                            {totalRecords}
                        </Badge>
                    )}
                </h6>
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    title="Actualizar"
                >
                    <FontAwesomeIcon icon="sync-alt" className={loading ? 'fa-spin' : ''} />
                </Button>
            </div>

            {loading && vacacionesSolicitadas.length === 0 ? (
                <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Cargando solicitudes...</p>
                </div>
            ) : vacacionesSolicitadas.length === 0 ? (
                <div className="text-center py-4 text-muted">
                    <FontAwesomeIcon icon="inbox" size="3x" className="mb-3 opacity-50" />
                    <p>No hay solicitudes de vacaciones registradas</p>
                </div>
            ) : (
                <>
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0">Fecha Inicio</th>
                                    <th className="border-0">Fecha Fin</th>
                                    <th className="border-0 text-center">D&iacute;as</th>
                                    <th className="border-0">Estado</th>
                                    <th className="border-0">Fecha Solicitud</th>
                                    <th className="border-0 text-center">Acci&oacute;n</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vacacionesSolicitadas.map((vacacion, index) => {
                                    const diasDesglosados = vacacion.dFechaVacaciones || [];
                                    const fechaInicio = diasDesglosados.length > 0 ? diasDesglosados[0] : null;
                                    const fechaFin = diasDesglosados.length > 0 ? diasDesglosados[diasDesglosados.length - 1] : null;

                                    return (
                                        <tr
                                            key={`${vacacion.idVacacion}-${index}`}
                                            onClick={() => handleRowClick(vacacion)}
                                            style={{ cursor: 'pointer' }}
                                            className="align-middle"
                                        >
                                            <td><SoftBadge>{fechaInicio ? formatDate(fechaInicio) : '-'}</SoftBadge></td>
                                            <td><SoftBadge>{fechaFin ? formatDate(fechaFin) : '-'}</SoftBadge></td>
                                            <td className="text-center">
                                                <SoftBadge bg="info" pill>{vacacion.iDiasSolicitados || 0}</SoftBadge>
                                            </td>
                                            <td>{getStatusBadge(vacacion.vEstado)}</td>
                                            <td><SoftBadge>{formatDate(vacacion.dCreatedDate)}</SoftBadge></td>
                                            <td className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    title="Generar Carta PDF"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        generateVacationLetterPDF(vacacion);
                                                    }}
                                                    disabled={isGenerating}
                                                >
                                                    {isGenerating ? (
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                    ) : (
                                                        <FontAwesomeIcon icon="file-pdf" />
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                            <small className="text-muted">
                                Mostrando p&aacute;gina {currentPage} de {totalPages} ({totalRecords} total)
                            </small>
                            <Pagination className="mb-0" size="sm">
                                {renderPaginationItems()}
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

VacationRequestsTable.propTypes = {
    perfil: PropTypes.object.isRequired,
    onVacationClick: PropTypes.func,
    refreshTrigger: PropTypes.number
};

export default VacationRequestsTable;